import { createContext, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import type { CompanyContext, CreatedCompany, UserProfile } from "@/shared/api/api-client";
import type { MentionCatalog } from "@/features/mentions/MentionInlineCatalog";
import type { Mention } from "@/shared/types/mention";

/**
 * O Context da casca e os hooks que o leem — SEM nenhum componente.
 *
 * Moravam dentro de `AppShellProvider.tsx`, e isso era a causa do
 * "useAppShell must be used within AppShellProvider" que aparecia do
 * nada em desenvolvimento e sumia com um F5 (2026-10-19). O Fast Refresh
 * do Vite só troca um módulo a quente, preservando o que está montado,
 * quando ele exporta SÓ componentes; um módulo que exporta hook junto é
 * reexecutado inteiro a cada edição que chega nele — a própria, ou a de
 * qualquer módulo sem componente que ele importa (`api-client`, `http`,
 * `oidc`, `navigation`…). Reexecutar o módulo rodava o `createContext`
 * de novo: o provider montado seguia escrevendo num Context e parte das
 * 37 telas que importam o hook passava a ler em OUTRO, conforme a ordem
 * em que o HMR as reavaliava. É o mesmo sintoma dos dois `createContext`
 * da época do RSC, por outro mecanismo.
 *
 * Aqui o Context nasce num arquivo que ninguém edita no dia a dia e que
 * não importa componente nenhum, então ele só é criado uma vez por
 * carregamento da página; e `AppShellProvider.tsx` passa a exportar só o
 * componente, que o Fast Refresh troca sem perder estado.
 */
/**
 * Como o drawer de menção abre. Sem opções (todo chamador antigo) ele é só
 * leitura; com `loadCatalog` ele edita a classificação — é o que a visão
 * Publicações pede (2026-10-22), e só ela. O `loadCatalog` é o mesmo do card
 * da visão por marca: devolve o catálogo da marca da menção, com a promessa
 * guardada pela página.
 */
export type OpenMentionOptions = {
  loadCatalog?: (mention: Mention) => Promise<MentionCatalog>;
};

export type AppShellContextValue = {
  token: string;
  profile: UserProfile;
  company: CompanyContext | null;
  // Só o TOTAL, pro contador da sidebar. Cada tela (Uploads, Menções,
  // Visão Geral) busca os próprios dados no contrato real, com o
  // polling que faz sentido pra ela — centralizar isso aqui obrigava
  // toda tela a compartilhar o mesmo recorte e a mesma cadência.
  mentionsTotal: number;
  openMention: (mention: Mention, options?: OpenMentionOptions) => void;
  /** Sobe a cada mudança feita pelo drawer de menção (correção salva,
   *  revisão marcada). A tela que lista menções o observa e refaz a
   *  página em silêncio — sem isso a linha ficava com o valor velho até
   *  um F5 (2026-10-03). */
  mentionChange: number;
  /** A menção que o drawer mudou na última subida de `mentionChange`
   *  (`null` = vincular/mover, que cria ou tira linha). A tela de menções
   *  usa pra atualizar só aquela linha, no lugar, sem refazer o filtro
   *  (2026-11-03). */
  mentionChangeId: number | null;
  refreshProfile: () => Promise<void>;
  logout: () => void;
};

// Antes este Context era guardado em `globalThis`, porque o `@vitejs/plugin-rsc`
// avaliava o módulo em grafos separados (`rsc`/`ssr`/`client`) e criava dois
// `createContext` distintos — o provider escrevia num, o `useContext` lia o
// outro. Sem RSC existe um grafo só, e a gambiarra deixou de ser necessária.
export const AppShellContext = createContext<AppShellContextValue | null>(null);

export function useAppShell(): AppShellContextValue {
  const context = useContext(AppShellContext);
  if (!context) throw new Error("useAppShell must be used within AppShellProvider");
  return context;
}

export function useRefreshProfileAfterCompanyCreated() {
  const { refreshProfile } = useAppShell();
  return useCallback(
    async (_created: CreatedCompany) => {
      await refreshProfile();
    },
    [refreshProfile]
  );
}

export function useSelectCompany() {
  const navigate = useNavigate();
  return useCallback(
    (company: CompanyContext) => {
      navigate(`/empresas/${company.id}/visao-geral`);
    },
    [navigate]
  );
}
